import { useCallback, useEffect, useRef } from "react";
import type { AnalyticsEvent } from "@connect/shared";
import { useApi } from "./api";

interface QueuedEvent {
  name: AnalyticsEvent;
  props?: Record<string, string | number | boolean>;
  session_id?: string;
  occurred_at: number;
}

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 10;

function ensureSessionId(): string {
  if (typeof window === "undefined") return "no-window";
  const key = "connect-session-id";
  let sid = window.sessionStorage.getItem(key);
  if (!sid) {
    sid = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.sessionStorage.setItem(key, sid);
  }
  return sid;
}

/**
 * Tiny event emitter that batches events and flushes to /events.
 *
 * - Queues events in memory.
 * - Flushes when queue reaches FLUSH_THRESHOLD or every FLUSH_INTERVAL_MS.
 * - On page hide, flushes via sendBeacon as a best-effort tail flush.
 * - Silently drops on auth failure — analytics should never break the UI.
 */
export function useTrack() {
  const api = useApi();
  const queue = useRef<QueuedEvent[]>([]);
  const sessionId = useRef<string>(ensureSessionId());
  const flushing = useRef(false);

  const flush = useCallback(async () => {
    if (flushing.current || queue.current.length === 0) return;
    const batch = queue.current.splice(0, queue.current.length);
    flushing.current = true;
    try {
      await api.post("/events", { events: batch });
    } catch {
      // Drop on failure; do not requeue to avoid amplifying outages.
    } finally {
      flushing.current = false;
    }
  }, [api]);

  // Periodic flush
  useEffect(() => {
    const interval = setInterval(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [flush]);

  // Tail flush on tab hide
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden" && queue.current.length > 0) {
        void flush();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [flush]);

  const track = useCallback(
    (name: AnalyticsEvent, props?: Record<string, string | number | boolean>) => {
      queue.current.push({
        name,
        props,
        session_id: sessionId.current,
        occurred_at: Date.now(),
      });
      if (queue.current.length >= FLUSH_THRESHOLD) {
        void flush();
      }
    },
    [flush],
  );

  return track;
}
